/* eslint-disable import/no-extraneous-dependencies */
import { forwardRef } from 'react';
import { IoMdClose } from 'react-icons/io';

import * as Dialog from '@radix-ui/react-dialog';

import { clsx } from 'clsx';

import { DialogInternalLoading } from './DialogInternalLoading';

import styles from './Dialog.module.scss';

import {
  type IDialogContentProps,
  type ITriggerProps,
  type ICloseDialogProps,
} from './@interfaces';

const Root = Dialog.Root;

Root.displayName = 'DialogRoot';

const Portal = Dialog.Portal;

Portal.displayName = 'DialogPortal';

const Trigger = Dialog.Trigger;

Trigger.displayName = 'DialogTrigger';

const Close = ({ className }: ICloseDialogProps) => {
  const classname = clsx(className);

  return (
    <Dialog.Close asChild className={classname}>
      <button type="button" className={styles.dialog__content_close}>
        <IoMdClose size={24} />
      </button>
    </Dialog.Close>
  );
};

const Content = forwardRef<HTMLDivElement, IDialogContentProps>(
  (
    {
      title = null,
      description = null,
      loading = null,
      className = '',
      children,
    },
    forwardedRef
  ) => {
    const classname = clsx(styles.dialog__content, className);

    function contentToRender() {
      const isContentLoading = loading && loading.enabled;

      if (isContentLoading) {
        return <DialogInternalLoading title={loading.title} />;
      }

      return <>{children}</>;
    }

    return (
      <>
        <Dialog.Overlay className={styles.dialog__content_overlay} />

        <Dialog.Content ref={forwardedRef} className={classname}>
          {title && (
            <Dialog.Title
              className={styles.dialog__content_title}
              hidden={title.hidden}
            >
              {title.text}
            </Dialog.Title>
          )}

          {description && (
            <Dialog.Description hidden={description.hidden}>
              {description.text}
            </Dialog.Description>
          )}

          {contentToRender()}
        </Dialog.Content>
      </>
    );
  }
);

Content.displayName = 'DialogContent';

export { Root, Content, Portal, Trigger, Close };
